import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchStudies } from "../services/studyService";
import { diagnosisLabel } from "../constants/diseaseOptions";
import { motion, AnimatePresence } from "framer-motion";
import { PROTOCOL_COMPARE_ENABLED } from "../config/featureFlags";
import { SharedComparisonModal } from "./study/ProtocolCompareButton";
import {
  ArrowLeft,
  Search,
  X,
  ChevronDown,
  HeartPulse,
  Activity,
  GitCompare,
} from "lucide-react";

// A protocol comparison an admin sent to coordinators (study page → Protocol
// versions → "Send comparison to coordinators"), staging flag only.
const sharedComparisonOf = (study) =>
  PROTOCOL_COMPARE_ENABLED && study?.sharedProtocolComparison?.comparisonId ? study.sharedProtocolComparison : null;
const sharedLabel = (p) =>
  p.newVersion ? `Protocol changes: Version ${p.newVersion} vs ${p.oldVersion}` : "Protocol changes";

const STATUS_STYLES = {
  ACTIVE: {
    chip: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
    dot: "bg-emerald-400",
    bar: "from-emerald-400 to-teal-400",
  },
  "START-UP": {
    chip: "bg-amber-400/15 text-amber-300 border-amber-400/30",
    dot: "bg-amber-400",
    bar: "from-amber-400 to-orange-400",
  },
  "ACTIVE/NR": {
    chip: "bg-sky-400/15 text-sky-300 border-sky-400/30",
    dot: "bg-sky-400",
    bar: "from-sky-400 to-blue-400",
  },
  "CLOSED/CLOSING": {
    chip: "bg-slate-400/15 text-slate-300 border-slate-400/30",
    dot: "bg-slate-400",
    bar: "from-slate-400 to-slate-500",
  },
  DEFAULT: {
    chip: "bg-purple-400/15 text-purple-300 border-purple-400/30",
    dot: "bg-purple-400",
    bar: "from-purple-400 to-indigo-400",
  },
};

const statusKey = (s) => (s || "").toString().trim().toUpperCase();

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const Studies = () => {
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [sharedFor, setSharedFor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [podFilter, setPodFilter] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const data = await fetchStudies();
      const statusRank = { ACTIVE: 1, "START-UP": 2, "ACTIVE/NR": 3, "CLOSED/CLOSING": 4 };
      const norm = (s) => (s || "").toString().trim().toUpperCase();
      const sorted = data.sort((a, b) => {
        const ra = statusRank[norm(a.STATUS)] ?? 99;
        const rb = statusRank[norm(b.STATUS)] ?? 99;
        if (ra !== rb) return ra - rb;
        return String(a.STUDY_ID || "").localeCompare(String(b.STUDY_ID || ""));
      });
      setStudies(sorted);
    };
    load();
  }, []);

  const uniqueStatuses = ["All", ...new Set(studies.map((s) => s.STATUS).filter(Boolean))];
  const uniquePods = ["All", ...new Set(studies.map((s) => s.POD).filter(Boolean))];

  const filteredStudies = studies.filter((study) => {
    const matchesSearch = `${study.STUDY_ID} ${study.NAME} ${study.SITE_ID}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || study.STATUS === statusFilter;
    const matchesPod = podFilter === "All" || study.POD === podFilter;
    return matchesSearch && matchesStatus && matchesPod;
  });

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      {/* Sticky header */}
      <header className="sticky top-0 z-30 backdrop-blur-2xl bg-slate-950/55 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.35)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                <Activity className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                Lung Innovations · RegX
              </div>
              <h1 className="text-sm sm:text-base font-semibold tracking-tight text-white leading-tight truncate">
                Study Library
              </h1>
            </div>
          </div>

          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 text-[10px] text-slate-300">
            <span className="font-semibold text-white">{filteredStudies.length}</span>
            &nbsp;of {studies.length}
          </span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 space-y-3">
        {/* Filter bar */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl px-2.5 py-2 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]">
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by Protocol, Name or Site ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition cursor-pointer"
                >
                  {uniqueStatuses.map((s) => (
                    <option key={s} value={s} className="bg-[#0b0820] text-slate-100">
                      {s === "All" ? "All Statuses" : s}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>

              <div className="relative">
                <select
                  value={podFilter}
                  onChange={(e) => setPodFilter(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition cursor-pointer"
                >
                  {uniquePods.map((p) => (
                    <option key={p} value={p} className="bg-[#0b0820] text-slate-100">
                      {p === "All" ? "All PODs" : p}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Study cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {filteredStudies.map((study, index) => {
            const key = statusKey(study.STATUS);
            const style = STATUS_STYLES[key] || STATUS_STYLES.DEFAULT;
            const goal = Number(study.GOAL) || 0;
            const enrolled = Number(study.ENROLLED) || 0;
            const progress = goal > 0 ? Math.min(100, Math.round((enrolled / goal) * 100)) : 0;

            return (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, type: "spring", stiffness: 260, damping: 22 }}
                onClick={() => setSelectedStudy(study)}
                className="group relative cursor-pointer rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-xl overflow-hidden hover:border-purple-300/30 hover:shadow-[0_10px_40px_-10px_rgba(139,92,246,0.45)] hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${style.bar} opacity-70`} />

                <div className="relative p-3.5 flex flex-col gap-2.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide border backdrop-blur-md ${style.chip}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                      {study.STATUS || "—"}
                    </span>
                    {study.DISEASE && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/[0.04] border border-white/10 text-slate-300">
                        <HeartPulse className="w-2.5 h-2.5 text-purple-300" />
                        {diagnosisLabel(study.DISEASE)}
                      </span>
                    )}
                    {sharedComparisonOf(study) && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSharedFor(study); }}
                        className="study-protocol-changes-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-500/15 border border-sky-400/30 text-sky-200 hover:bg-sky-500/25"
                      >
                        <GitCompare className="w-2.5 h-2.5" />
                        {sharedLabel(sharedComparisonOf(study))}
                      </button>
                    )}
                  </div>

                  <div>
                    <div className="text-[10px] font-mono tracking-wider text-purple-300/80 uppercase">
                      {study.STUDY_ID || "—"}
                    </div>
                    <h3 className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-purple-100 transition">
                      {study.NAME || "Untitled Study"}
                    </h3>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5 pt-0.5">
                    <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">Goal</div>
                      <div className="text-sm font-bold text-white leading-tight">{study.GOAL || "—"}</div>
                    </div>
                    <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">Enrolled</div>
                      <div className="text-sm font-bold text-emerald-300 leading-tight">{study.ENROLLED || "—"}</div>
                    </div>
                    <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">SF</div>
                      <div className="text-sm font-bold text-rose-300 leading-tight">{study.SCREEN_FAILURES || "—"}</div>
                    </div>
                  </div>

                  {goal > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-slate-400 uppercase tracking-wider font-medium">Enrollment</span>
                        <span className="text-slate-200 font-semibold">{progress}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${style.bar} transition-all duration-500`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-1.5 border-t border-white/5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-[9px] font-bold text-white shrink-0">
                        {(study.LEAD_CRC || "—").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium leading-none">Lead CRC</div>
                        <div className="text-[11px] text-slate-200 truncate">{study.LEAD_CRC || "—"}</div>
                      </div>
                    </div>
                    {study.SITE_ID && (
                      <span className="text-[10px] font-mono text-slate-400 px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/5">
                        {study.SITE_ID}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {filteredStudies.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 text-center">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center mb-3">
                <Search className="w-5 h-5 text-purple-300" />
              </div>
              <h3 className="text-sm font-semibold text-white">No studies match</h3>
              <p className="text-xs text-slate-400 mt-1">Try clearing filters or adjusting your search.</p>
            </div>
          )}
        </div>

        <div className="h-8" />
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedStudy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
            onClick={(e) => { if (e.target === e.currentTarget) setSelectedStudy(null); }}
          >
            <motion.div
              initial={{ y: 18, scale: 0.98, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 18, scale: 0.98, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] flex flex-col text-slate-100"
              role="dialog"
              aria-modal="true"
            >
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-purple-600/20 blur-[120px]" />
                <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-600/20 blur-[120px]" />
              </div>

              {/* Header */}
              <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
                  <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">Study Details</div>
                  <h2 className="text-base font-semibold tracking-tight text-white leading-tight truncate">
                    {selectedStudy.NAME}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedStudy(null)}
                  className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="relative overflow-y-auto flex-1 px-5 py-4 space-y-4">
                {/* Meta row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: "Study ID", value: selectedStudy.STUDY_ID },
                    { label: "Site", value: selectedStudy.SITE_ID },
                    { label: "Lead CRC", value: selectedStudy.LEAD_CRC },
                    { label: "Status", value: selectedStudy.STATUS },
                  ].map(({ label, value }) => (
                    <div key={label} className="rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">{label}</div>
                      <div className="text-sm font-semibold text-white">{value || "—"}</div>
                    </div>
                  ))}
                </div>

                {sharedComparisonOf(selectedStudy) && (
                  <div className="study-protocol-changes-panel rounded-xl border border-sky-400/30 bg-sky-500/10 px-3 py-2.5 flex flex-wrap items-center gap-2">
                    <GitCompare className="w-4 h-4 text-sky-300 shrink-0" />
                    <span className="flex-1 min-w-0 text-xs text-slate-200">
                      An admin shared what changed in the protocol ({sharedLabel(sharedComparisonOf(selectedStudy)).replace("Protocol changes: ", "")}).
                    </span>
                    <button
                      type="button"
                      onClick={() => { setSharedFor(selectedStudy); setSelectedStudy(null); }}
                      className="study-protocol-changes-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-xs font-semibold text-white ring-1 ring-white/20"
                    >
                      See protocol changes
                    </button>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Enrollment Goal", value: selectedStudy.GOAL || 0, cls: "text-purple-300" },
                    { label: "Enrolled", value: selectedStudy.ENROLLED || 0, cls: "text-emerald-300" },
                    { label: "Screen Failures", value: selectedStudy.SCREEN_FAILURES || 0, cls: "text-rose-300" },
                  ].map(({ label, value, cls }) => (
                    <div key={label} className="rounded-xl bg-white/[0.03] border border-white/10 p-3 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1">{label}</div>
                      <div className={`text-2xl font-bold ${cls}`}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Eligibility criteria */}
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { label: "Inclusion Criteria", value: selectedStudy.INCLUSION, accent: "from-emerald-400 to-teal-400" },
                    { label: "Exclusion Criteria", value: selectedStudy.EXCLUSION, accent: "from-rose-400 to-pink-400" },
                  ].map(({ label, value, accent }) => (
                    <div key={label} className="relative rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-xl p-4 overflow-hidden">
                      <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${accent} opacity-70 pointer-events-none`} />
                      <h5 className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-300 mb-2">{label}</h5>
                      <pre className="whitespace-pre-wrap text-xs text-slate-300 font-[Manrope] leading-relaxed">
                        {value || "—"}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {sharedFor && sharedComparisonOf(sharedFor) && (
        <SharedComparisonModal study={sharedFor} pointer={sharedComparisonOf(sharedFor)} onClose={() => setSharedFor(null)} />
      )}
    </div>
  );
};

export default Studies;
